import { useQueryClient } from "@tanstack/react-query";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { TlxMcpControllerService } from "@/api";
import { Loading } from "@/components/Loading";
import useQuery from "@/hooks/useQuery";
import {
  checkErrorOf,
  environmentFromMcpUser,
  isTenantEnforcementKnown,
  setTenantEnforcement,
  unknownTenantEnforcement,
} from "./tenantEnforcement";

const RefreshContext = createContext<() => Promise<void>>(async () => {});

export const useRefreshTenantEnforcement = () => useContext(RefreshContext);

const DEPENDENT_QUERIES = [
  "getEstateReadinessUsingGet",
  "getEnforcementEnvironmentUsingGet",
  "getAgentOnboardingProgressUsingGet",
  "getAgentEstateSummaryUsingGet",
  "listEstateAgentsUsingGet",
  "getEstateAgentUsingGet",
  "resolveAgentPolicyRecommendationsUsingPost",
  "resolveAgentPolicyCoverageUsingPost",
  "getPolicyReviewUsingPost",
  "previewAgentPoliciesUsingPost",
  "getAgentFlowSummaryUsingGet",
  "listAgentAccessFlowsUsingGet",
  "getAgentAccessFlowUsingGet",
] as const;

/**
 * Runs outside MockScope: this GET must always read the saved tenant record.
 *
 * Only the first read of a session holds the page behind a loader. After that
 * every route mount renders straight away from the committed snapshot while
 * the refetch reconciles in the background - a change re-renders subscribers
 * and invalidates the queries derived from it.
 */
const TenantEnforcementBridge: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [ready, setReady] = useState(isTenantEnforcementKnown);
  const queryClient = useQueryClient();
  const { data, error, isLoading, isFetching, isError, refetch } = useQuery({
    method: TlxMcpControllerService.getMcpUserUsingGet,
    params: {},
    options: { staleTime: 0, refetchOnMount: "always" },
  });

  const commit = useCallback(
    (next: ReturnType<typeof environmentFromMcpUser>) => {
      if (setTenantEnforcement(next)) {
        DEPENDENT_QUERIES.forEach(key => {
          void queryClient.invalidateQueries({
            queryKey: [key],
            refetchType: "all",
          });
        });
      }
      setReady(true);
    },
    [queryClient],
  );

  useEffect(() => {
    if (isLoading || isFetching) return;
    commit(
      isError
        ? unknownTenantEnforcement(checkErrorOf(error))
        : environmentFromMcpUser(data),
    );
  }, [commit, data, error, isError, isFetching, isLoading]);

  // Stable, so the context value does not re-render every consumer.
  const refresh = useCallback(async () => {
    const result = await refetch();
    commit(
      result.isError
        ? unknownTenantEnforcement(checkErrorOf(result.error))
        : environmentFromMcpUser(result.data),
    );
  }, [refetch, commit]);

  if (!ready) return <Loading isLoading />;
  return (
    <RefreshContext.Provider value={refresh}>
      {children}
    </RefreshContext.Provider>
  );
};

export default TenantEnforcementBridge;
